import React from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { PopoverCard } from "components/ActivityLog";
import ListTable from "components/ListTable";
import { formatNumber, isEmpty } from "utils";

/**
 * RateScoreDisplay
 *
 * @params {Object}    rateData
 * @params {String}    currency
 */
const RateScoreDisplay = ({ rateData, currency }) => {
  if (!rateData || !currency) return <FormattedMessage id="Global.NA" />;

  return (
    <PopoverCard width="35rem">
      <ListTable data={TABLE_DATA(rateData, currency)} />
    </PopoverCard>
  );
};

const TABLE_DATA = (
  {
    missionRoleTargetRateUsd,
    profileHourlyRateUsd,
    minimumApplicantHourlyRateUsd,
    minimumAssignedHourlyRateUsd,
    consideredHourlyRateUsd,
    negotiatedHourlyRateUsd,
    applicantHourlyRateUsd,
    consideredHourlyRateUsdDelta,
    applicantHourlyRateUsdDelta,
  },
  currency
) =>
  [
    {
      label: <FormattedMessage id="Missions.MissionCandidateList.RateScore.TargetRate" />,
      value: <RateWithFallbackText rate={missionRoleTargetRateUsd} currency={currency} />,
    },
    {
      label: <FormattedMessage id="Missions.MissionCandidateList.RateScore.PreferredRate" />,
      value: <RateWithFallbackText rate={profileHourlyRateUsd} currency={currency} />,
    },
    {
      label: <FormattedMessage id="Missions.MissionCandidateList.RateScore.MinApplicationRate" />,
      value: <RateWithFallbackText rate={minimumApplicantHourlyRateUsd} currency={currency} />,
    },
    {
      label: <FormattedMessage id="Missions.MissionCandidateList.RateScore.MinAssignedRate" />,
      value: <RateWithFallbackText rate={minimumAssignedHourlyRateUsd} currency={currency} />,
    },
    {
      label: <FormattedMessage id="Missions.MissionCandidateList.RateScore.ApplicantRate" />,
      value: (
        <RateWithFallbackText rate={applicantHourlyRateUsd} delta={applicantHourlyRateUsdDelta} currency={currency} />
      ),
    },
    {
      label: <FormattedMessage id="Missions.MissionCandidateList.RateScore.NegotiatedRate" />,
      value: <RateWithFallbackText rate={negotiatedHourlyRateUsd} currency={currency} />,
    },
    {
      label: <FormattedMessage id="Missions.MissionCandidateList.RateScore.ConsideredRate" />,
      value: (
        <RateWithFallbackText rate={consideredHourlyRateUsd} delta={consideredHourlyRateUsdDelta} currency={currency} />
      ),
    },
  ].filter(Boolean);

const RateWithFallbackText = ({ rate, currency, delta }) =>
  rate ? (
    <div css={rateContainerStyles}>
      <div>
        <FormattedMessage id="Global.HourlyRateWithCurrency" values={{ rate: formatNumber(rate, "", 2), currency }} />
      </div>
      {!isEmpty(delta) && (
        <div>
          <FormattedMessage
            id="Global.Parenthesis"
            values={{
              value: delta > 0 ? `+${formatNumber(delta, "", 2)}` : formatNumber(delta, "", 2),
            }}
          />
        </div>
      )}
    </div>
  ) : (
    <FormattedMessage id="Global.EmDash" />
  );

const rateContainerStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

RateWithFallbackText.propTypes = {
  rate: PropTypes.string,
  currency: PropTypes.string,
  delta: PropTypes.string,
};

RateScoreDisplay.propTypes = {
  rateData: PropTypes.object.isRequired,
  currency: PropTypes.string,
};

export default RateScoreDisplay;
